import {
  Component, Host, h, Method, State, Element, Event, EventEmitter, Prop,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

@Component({
  tag: 'ui-dialog',
  styleUrl: 'ui-dialog.scss',
  shadow: true,
})
export class UiDialog {
  @Element() el: HTMLUiDialogElement;

  /**
   * Indicates whether the background should be blurred to hide content behind the dialog.
   * @type {boolean}
   * @default false
   */
  @Prop({ attribute: 'blurBackground' }) blurBackground = false;

  /**
   * Indicates whether the dialog should restrict users from closing via close icon / esc key press.
   * When true, the close icon is hidden and esc key disabled.
   * @type {boolean}
   * @default false
   */
  @Prop({ attribute: 'restrictClose' }) restrictClose = false;

  /**
   * Indicates whether the dialog should be open by default.
   * The dialog is otherwise closed by default.
   * @type {boolean}
   * @default false
   */
  @Prop({ attribute: 'openByDefault' }) openByDefault = false;

  @State() isClosed = !this.openByDefault;

  @Event({ eventName: 'uiDialogShow' }) showEvent: EventEmitter;

  @Event({ eventName: 'uiDialogClose' }) closeEvent: EventEmitter;

  @Method()
  async show(): Promise<void> {
    this.isClosed = false;
    this.showEvent.emit();
    window.setTimeout(() => this.refDialogContent.focus(), 100);
  }

  // @TODO PPCMS-1764 add focus to elem which opened the modal
  @Method()
  async close(): Promise<void> {
    this.isClosed = true;
    this.closeEvent.emit();
  }

  private refDialogContent: HTMLUiContainerElement;

  private handleClick(): void {
    if (!this.restrictClose) {
      this.close();
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.restrictClose && e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  }

  // @TODO PPCMS-1764 add a11y roles, states (...) attributes
  // @TODO PPCMS-1764 add focus trapping
  render() {
    return (
      <Host>
        <div
          class={{ dialog: true, 'dialog--hide': this.isClosed }}
          onKeyDown={(e) => this.handleKeyDown(e)}
        >
          <div
            tabIndex={-1}
            class={{ dialog__overlay: true, 'hide-bg': this.blurBackground }}
            onClick={() => this.handleClick()}
          />
          <ui-container
            tabIndex={0}
            class='dialog__content'
            variant='white'
            ref={(el) => { this.refDialogContent = el; }}
            noPadding
          >
            {!this.restrictClose && <ui-button
              aria-label='Close dialog'
              class='dialog__close'
              variant='transparent'
              icon='close'
              onClick={() => this.handleClick()}
            />}
            <slot />
          </ui-container>
        </div>
      </Host>
    );
  }
}

versionComponent(UiDialog);
